import PropTypes from 'prop-types';
import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Controller, Mousewheel, Pagination, Navigation } from 'swiper';
import cls from 'classnames';

import { getContent, isDDEnv, isWHEnv } from '@/utils/helper';
import { analytics, getClickCtaDetail } from '@/utils/analytics';

import { resetPositionX, setCutGrabX } from './cutGrab';
import CutsNavigation from './CutsNavigation';

import Text from '@/atoms/Text/Text';
import RichText from '@/molecules/RichText/RichText';
import ButtonDropdown from '@/molecules/ButtonDropdown/ButtonDropdown';
import { Cta } from '@/molecules/CTA/index';
import { default as Navs } from '@/organisms/Navigation/Navigation';

import { TEXT_BASE_CLASS } from '@/atoms/Text/Text.constants';
import { CtfCtaStyle } from '@/utils/constants';
import Icon from '@/atoms/Icon/Icon';

const SWIPER_MODULES = [A11y, Controller, Mousewheel, Pagination];

const SWIPER_PAGINATION = {
  clickable: true,
  el: '.aaco-cuts-illustrations__pagination',
  type: 'bullets',
};

const SWIPER_NAVIGATION = {
  nextEl: '.aaco-cuts-illustrations__next',
  prevEl: '.aaco-cuts-illustrations__prev',
};

const isWH = isWHEnv();
const isDD = isDDEnv();

function CutsHeader({ assets = [], entries = [], fields = null, slug }) {
  const floatingCutRef = useRef(null);
  const cowRef = useRef(null);
  const cutDescriptionRef = useRef(null);
  const cowSvgRef = useRef(null);

  const [swiper, setSwiper] = useState(null);

  const { title, subtitle, backgroundImage, cuts, navigation } = fields;
  const bg = getContent(backgroundImage?.sys.id, assets);
  const navigationFields = navigation
    ? getContent(navigation.sys.id, entries).fields
    : null;
  const cutDetails = useMemo(() => {
    const v = {};
    cuts
      .map((c) => getContent(c.sys.id, entries))
      .filter((c) => c && c.fields)
      .forEach(
        (c) =>
          (v[c.fields.slug] = {
            ...c.fields,
            illustrations: c.fields.illustrations
              ? c.fields.illustrations
                  .map((i) => getContent(i.sys.id, entries).fields)
                  .filter((i) => i?.name?.toLowerCase() !== 'n/a')
                  .map((i) => ({
                    ...i,
                    image: getContent(i?.image.sys.id, assets)?.fields.file.url,
                  }))
              : [],
          })
      );

    return v;
  }, [assets, cuts, entries]);

  const [activeCut, setActiveCut] = useState(cutDetails.chuck);

  const onCutChange = useCallback(
    (slug) => {
      if (!cutDetails?.[slug]) {
        return;
      }
      setActiveCut(cutDetails[slug]);
    },
    [cutDetails]
  );

  useEffect(() => {
    if (cowRef && cutDescriptionRef) {
      // centering cut on mobile
      if (window?.innerWidth < 768 && !isWH) {
        cowRef.current.style.height = `fit-content`;
        cowSvgRef.current.style.height = `fit-content`;
      }
    }
  }, [cowRef, cutDescriptionRef, cowSvgRef]);

  useEffect(() => {
    if (floatingCutRef) {
      setCutGrabX(floatingCutRef);
    }
  }, [floatingCutRef]);

  useEffect(() => {
    if (floatingCutRef && activeCut) resetPositionX(floatingCutRef);
  }, [activeCut, floatingCutRef]);

  return (
    <div className={cls('aaco-cuts-header__wrapper flex flex-col h-full w-full pb-8 md:pb-32 xl:pb-52 2xl:pb-20')}>
      <div
        className={
          cls('aaco-header aaco-cuts-header h-auto md:h-[85vh] bg-warm-grey relative md:mb-[15vh]',
            {
              '!bg-transparent': isWH,
            }
          )
        }
        aria-label={title}
      >
        {navigationFields && (
          <Navs
            assets={assets}
            hasBackground={false}
            fields={navigationFields}
            entries={entries}
            slug={slug}
          />
        )}
        <div className={cls('aaco-header__content container mx-auto text-white text-center flex flex-col relative', {
          'h-full': !isWH,
        })}>
          {!isWH && (
            <div className="min-h-[85px] md:min-h-[20%] w-full" />
          )}
          <div className="aaco-header__titles w-full relative">
            <Text
              tag="h1"
              style={cls({
                'text-acacia': true,
                'text-center mt-10 md:mt-12 mb-2': !isWH,
                'text-left text-[44px] md:text-[64px] border-b border-black': isWH,
              })}
            >
              {title}
            </Text>
            <div className="text-center md:absolute md:top-0 md:right-0 flex justify-center md:justify-self-center">

            </div>
            {subtitle && (
              <div className={cls('hidden md:block text-center text-acacia text-body', {
                '!block !text-left text-base mt-[20px]': isWH,
              })}>
                {subtitle}
              </div>
            )}
          </div>
          <div
            className={cls('aaco-header__image-desktop', {
              'h-fit md:h-[1px] md:flex-1 pt-5 md:pt-2 flex justify-center md:relative items-center': !isWH,
              'bg-warm-grey flex flex-col border border-black h-[50vh] md:h-[60vh] mt-[40px]': isWH,
            })}
            ref={cowRef}
          >
            <div className="relative md:p-6 p-3 md:self-end self-center">
              <ButtonDropdown />
            </div>
            <CutsNavigation
              cuts={cuts}
              assets={assets}
              entries={entries}
              onChange={onCutChange}
              backgroundSrc={bg.fields.file.url}
              svgRef={cowSvgRef}
            />
          </div>

        </div>

        <div className={cls({
          'flex flex-col md:flex-row md:justify-between container m-auto mt-10': isWH,
        })}>
          {!isWH && (
            <div className="md:hidden self-end">
              <div
                className={cls(
                  'bg-acacia h-auto w-auto',
                  'translate-y-[30px]',
                  'ml-[30px] mr-8 mt-[13px] px-[30px] pt-8 pb-[35px]'
                )}
              >
                <CutsTitle
                  cutDescriptionRef={cutDescriptionRef}
                  activeCut={activeCut}
                />
              </div>
            </div>
          )}

          <div
            className={cls({
              'hidden md:block absolute bottom-[-15vh]': !isWH,
              'h-auto min-h-[35vh] w-1/4 bg-acacia': !isWH,
              'pt-[44px] pl-[50px] pr-[52px] pb-10 mt-0': !isWH,
              'md:w-[30%]': isWH,
            })}
          >
            <CutsTitle
              cutDescriptionRef={cutDescriptionRef}
              activeCut={activeCut}
            />
          </div>

          <div
            className={cls(
              'aaco-cuts-illustrations md:aaco-cuts-illustrations-desktop',
              'bg-transparent',
              'w-full',
              'relative',
              {
                'pl-[10%] md:ml-[25%] pb-[34px] md:w-3/4': !isWH,
                'md:w-[60%] border-t border-black md:mt-16': isWH,
              }
            )}
          >
            <div className={cls(
              !isWH && 'md:w-[70%]',
              isWH && 'md:w-[80%] md:m-auto',
            )}>
              <Swiper
                modules={[Navigation, A11y, Controller, Mousewheel]}
                navigation={SWIPER_NAVIGATION}
                onSwiper={setSwiper}
                breakpoints={{
                  // when window width is >= 480px
                  360: {
                    slidesPerView: 3
                  },
                  480: {
                    spaceBetween: 50,
                    slidesPerView: 3
                  },
                  // when window width is >= 640px
                  640: {
                    slidesPerView: 'auto',
                    spaceBetween: 55,
                  },
                }}
              >
                <div className="w-full md:w-[50%] md:pl-[25%]">
                  {activeCut?.illustrations.map((i, index) => (
                    <SwiperSlide key={index}>
                      <CutsIllustration key={index} cut={i} />
                    </SwiperSlide>
                  ))}
                </div>
              </Swiper>

              <div className="swiper-control flex flex-row justify-between absolute w-full px-4 left-0 md:px-8 top-1/2 z-10">
                <div className="aaco-cuts-illustrations__prev z-10">
                  <Icon name="chevron-left" height={16} width={16} />
                </div>
                <div className="aaco-cuts-illustrations__next z-10">
                  <Icon name="chevron-right" height={16} width={16} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// @TODO: define real props for the Header based on it requirements
CutsHeader.propTypes = {
  assets: PropTypes.array,
  entries: PropTypes.array,
  fields: PropTypes.object,
  slug: PropTypes.string,
};

export default CutsHeader;

function CutsTitle({ cutDescriptionRef, activeCut, entries }) {
  return (
    <div
      className={cls('aaco-cuts-title', {
        'text-white': !isWH,
        'text-black': isWH,
      })}
      ref={cutDescriptionRef}
    >
      <div className={cls(
        'mb-5 md:mb-[17px]',
        isWH && 'pb-[20px] mb-[20px] border-b border-black'
      )}>
        <Text tag="h4" style="text-left">
          {activeCut?.title}
          <span className="text-white font-body text-body align-super ml-2">
            {activeCut?.illustrations.length}
          </span>
        </Text>
      </div>
      <div className="mb-[15px] md:mb-[43px]">
        {activeCut?.secondaryDescription &&
          activeCut?.secondaryDescription.content.length > 0 &&
          activeCut?.secondaryDescription.content.map(
            ({ nodeType, content }, index) => (
              <RichText
                entries={entries}
                nodeType={nodeType}
                content={content}
                key={index}
                richTextStyle={{
                  paragraph: {
                    customFont: TEXT_BASE_CLASS['body'] + (!isWH ? ' text-white': ''),
                    blockquoteFont: TEXT_BASE_CLASS['blockquote'],
                  },
                }}
              />
            )
          )}
      </div>
      {activeCut?.pages?.length > 0 && (
        <Cta
          buttonProps={{
            name: 'Learn more',
            type: CtfCtaStyle.SECONDARY,
            style: 'hidden md:block ',
          }}
          iconProps={{
            hideIcon: true,
          }}
          linkProps={{
            url: `#${activeCut.slug}`,
          }}
          onClick={(e, { localUrl, url, name }) =>
            analytics({
              window,
              document,
              detail: getClickCtaDetail(e, { url, localUrl, name }),
            })
          }
        />
      )}
    </div>
  );
}

function CutsIllustration({ cut: { image, name } } = {}) {
  return (
    <div className="aaco-cuts-illustration flex flex-col bg-warmer-grey text-center items-center w-auto mr-[5px] pt-[15px] pb-[20px] py-0">
      <img
        src={`${image.replace('downloads', 'images')}?w=300&fm=webp&q=80`}
        className="w-[90px] h-[90px] md:h-full md:max-w-fit md:w-full object-contain mix-blend-multiply"
        alt={name}
      />
      <Text
        tag="tag"
        style="ml-[12px] md:ml-0 w-full md:w-auto text-dark-warm-grey"
      >
        {name}
      </Text>
    </div>
  );
}
